import React, { useEffect, useState } from 'react'
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Paper, Stack,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ResourceList from './ResourceList'
import Markdown from './Markdown'
import CatalogBanner from './CatalogBanner'

// The expansion set for a given mode: 'first' (only the top version, the admin
// default), 'all' (every version) or 'none' (collapsed).
function expandedFor(versions, mode) {
  if (mode === 'all') return new Set(versions.map((v) => v.id))
  if (mode === 'none') return new Set()
  return versions.length ? new Set([versions[0].id]) : new Set()
}

// Renders one model: its name, description and version accordions. Versions
// keep the API order (by position, then name) so the public page matches the
// admin. Shared by the full product page and the scoped /view pages.
// `expandMode` (driven by the product-page selector) sets which versions start
// open: 'first' (default), 'all' or 'none'.
export default function ModelSection({ model, expandMode = 'first' }) {
  const versions = model.versions

  // Controlled expansion so the selector can drive it while the visitor can
  // still open/close individual versions afterwards.
  const [expanded, setExpanded] = useState(() => expandedFor(versions, expandMode))

  // Re-apply the set whenever the selector changes (or the model changes).
  useEffect(() => {
    setExpanded(expandedFor(versions, expandMode))
  }, [expandMode, versions])

  const handleChange = (id) => (_event, isExpanded) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (isExpanded) next.add(id)
      else next.delete(id)
      return next
    })
  }

  return (
    <Box sx={{ mb: 4 }}>
      <CatalogBanner src={model.image_url} alt={model.name} sx={{ maxHeight: 220, mb: 2 }} />
      <Typography variant="h5" gutterBottom>{model.name}</Typography>
      {model.description && (
        <Markdown sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
          {model.description}
        </Markdown>
      )}

      {versions.length === 0 ? (
        <Typography color="text.secondary">Версій ще немає.</Typography>
      ) : (
        versions.map((version) => (
          <Accordion
            key={version.id}
            component={Paper}
            disableGutters
            expanded={expanded.has(version.id)}
            onChange={handleChange(version.id)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack spacing={0.25} sx={{ pr: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{version.name}</Typography>
                {version.description && (
                  <Markdown
                    disableLinks
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    {version.description}
                  </Markdown>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <ResourceList resources={version.resources} />
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  )
}
